import { useState, useRef, useEffect } from 'react';
import { ChevronDown, SlidersHorizontal, X } from 'lucide-react';

const SORT_OPTIONS = [
  { label: 'Newest First', value: 'createdAt-DESC' },
  { label: 'Price: Low to High', value: 'price-ASC' },
  { label: 'Price: High to Low', value: 'price-DESC' },
  { label: 'Popular', value: 'popular-DESC' },
];

const PRICE_OPTIONS = [
  { label: 'All Prices', min: 0, max: 99999 },
  { label: 'Under ₹199', min: 0, max: 199 },
  { label: '₹200 – ₹499', min: 200, max: 499 },
  { label: '₹500 – ₹999', min: 500, max: 999 },
  { label: 'Above ₹1000', min: 1000, max: 99999 },
];

function DropdownPill({ label, active, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-all whitespace-nowrap
          ${active
            ? 'bg-brand-dark text-white border-brand-dark'
            : 'bg-white text-gray-700 border-gray-200 hover:border-brand-dark'
          }`}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-[999] min-w-[180px]">
          {children}
        </div>
      )}
    </div>
  );
}

export default function FilterBar({
  // sort
  sortValue = 'createdAt-DESC',
  onSortChange,
  // price
  priceMin = 0,
  priceMax = 99999,
  onPriceChange,
  // category pills (optional — for all-products page)
  categories = [],
  selectedCategory = '',
  onCategoryChange,
  // active filter chips
  activeSearch = '',
  onClearSearch,
  // total
  total,
  loading,
  // mobile filter button (optional)
  onFiltersClick,
  activeFilterCount = 0,
}) {
  const sortLabel = SORT_OPTIONS.find(o => o.value === sortValue)?.label || 'Sort By';
  const priceLabel = PRICE_OPTIONS.find(o => o.min === priceMin && o.max === priceMax)?.label;
  const isPriceActive = priceMin !== 0 || priceMax !== 99999;

  return (
    <div className="mb-6">
      {/* Pill row */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Filters button (mobile) */}
        {onFiltersClick && (
          <button
            onClick={onFiltersClick}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-all flex-shrink-0
              ${activeFilterCount > 0
                ? 'bg-brand-dark text-white border-brand-dark'
                : 'bg-white text-gray-700 border-gray-200 hover:border-brand-dark'
              }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-white text-brand-dark rounded-full w-4 h-4 text-[10px] flex items-center justify-center font-bold ml-0.5">
                {activeFilterCount}
              </span>
            )}
          </button>
        )}

        {/* Sort By */}
        <DropdownPill label={sortLabel} active={sortValue !== 'createdAt-DESC'}>
          {SORT_OPTIONS.map(o => (
            <button key={o.value} onClick={() => onSortChange(o.value)}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50
                ${sortValue === o.value ? 'font-semibold text-brand-dark bg-brand-yellow-light' : 'text-gray-700'}`}>
              {o.label}
            </button>
          ))}
        </DropdownPill>

        {/* Price */}
        {onPriceChange && (
          <DropdownPill label={isPriceActive ? (priceLabel || 'Price') : 'Price'} active={isPriceActive}>
            {PRICE_OPTIONS.map(o => (
              <button key={o.label} onClick={() => onPriceChange(o.min, o.max)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50
                  ${priceMin === o.min && priceMax === o.max ? 'font-semibold text-brand-dark bg-brand-yellow-light' : 'text-gray-700'}`}>
                {o.label}
              </button>
            ))}
          </DropdownPill>
        )}

        {/* Category pills — shown on all-products page */}
        {categories.length > 0 && onCategoryChange && (
          <DropdownPill
            label={categories.find(c => c.id == selectedCategory)?.name || 'Category'}
            active={!!selectedCategory}
          >
            <button onClick={() => onCategoryChange('')}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50
                ${!selectedCategory ? 'font-semibold text-brand-dark bg-brand-yellow-light' : 'text-gray-700'}`}>
              All Categories
            </button>
            {categories.map(cat => (
              <button key={cat.id} onClick={() => onCategoryChange(cat.id)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50
                  ${selectedCategory == cat.id ? 'font-semibold text-brand-dark bg-brand-yellow-light' : 'text-gray-700'}`}>
                {cat.name}
              </button>
            ))}
          </DropdownPill>
        )}

        {/* Divider + count */}
        <span className="flex-shrink-0 text-xs text-gray-400 ml-auto whitespace-nowrap">
          {loading ? '' : `${total ?? 0} products`}
        </span>
      </div>

      {/* Active filter chips */}
      {(activeSearch || selectedCategory || isPriceActive) && (
        <div className="flex flex-wrap gap-2 mt-3">
          {activeSearch && (
            <span className="flex items-center gap-1 bg-brand-yellow-light text-brand-dark text-xs px-3 py-1.5 rounded-full font-medium">
              "{activeSearch}"
              <button onClick={onClearSearch} className="ml-0.5 hover:opacity-70"><X className="w-3 h-3" /></button>
            </span>
          )}
          {isPriceActive && (
            <span className="flex items-center gap-1 bg-brand-yellow-light text-brand-dark text-xs px-3 py-1.5 rounded-full font-medium">
              {priceLabel}
              <button onClick={() => onPriceChange(0, 99999)} className="ml-0.5 hover:opacity-70"><X className="w-3 h-3" /></button>
            </span>
          )}
          {selectedCategory && categories.find(c => c.id == selectedCategory) && (
            <span className="flex items-center gap-1 bg-brand-yellow-light text-brand-dark text-xs px-3 py-1.5 rounded-full font-medium">
              {categories.find(c => c.id == selectedCategory)?.name}
              <button onClick={() => onCategoryChange('')} className="ml-0.5 hover:opacity-70"><X className="w-3 h-3" /></button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
